/**
 * HTML PRZENOŚNY — most między edytorem a tym, co realnie zobaczy pracownik.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *  PO CO TO ISTNIEJE (klasa błędu, która w tym projekcie wystąpiła już dwa razy)
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * Quill zapisuje część formatowania jako KLASY (`ql-align-center`, `ql-indent-2`) i atrybuty
 * (`data-list="bullet"`), a nie jako style. To działa dopóki treść renderuje się w miejscu,
 * które ma wgrany arkusz Quilla. Regulamin renderuje się GDZIE INDZIEJ:
 *
 *   `TermsOfServiceModal` → `sanitizeHtml()` → `TrescHtml` (OknoBramki)
 *
 * a `sanitizeHtml` (utils/sanitizeHtml.js) NIE przepuszcza atrybutu `class` (nie ma go na liście
 * `ALLOWED_ATTR`) ani żadnego `data-*` (`ALLOW_DATA_ATTR: false`). Skutek bez tej warstwy:
 * administrator ustawia wyśrodkowanie, wcięcie i listę punktowaną, w edytorze widzi je poprawnie,
 * a pracownik dostaje tekst DO LEWEJ, BEZ WCIĘĆ i z listą punktowaną zamienioną na NUMEROWANĄ
 * (Quill 2 buduje każdą listę na `<ol>`, a rodzaj trzyma w `data-list`).
 *
 * Dokładnie ten sam błąd opisuje `hrQuillSetup.js` dla wzorów pism HR („formatowanie wyglądało
 * dobrze w edytorze i ZNIKAŁO na wydruku", usterka L-01).
 *
 * ROZWIĄZANIE: treść zapisujemy jako HTML PRZENOŚNY — semantyczne znaczniki + style INLINE, bez
 * jednej klasy CSS. Taki dokument wygląda tak samo w oknie akceptacji, w podglądzie administratora,
 * w historii i w każdym przyszłym miejscu (mail, PDF), bo nie zależy od żadnego arkusza.
 *
 * Funkcje są CZYSTE i operują na osobnym dokumencie (`createHTMLDocument`), więc nic się nie
 * wykonuje, nic nie pobiera i da się je przetestować bez przeglądarki.
 */

/** Rozmiary czcionki jak w edytorze tekstu — jednostka punktowa, bo tak myśli osoba pisząca regulamin. */
export const ROZMIARY_PT = [
    { label: "8", value: "8pt" },
    { label: "9", value: "9pt" },
    { label: "10", value: "10pt" },
    { label: "11", value: "11pt" },
    { label: "12", value: "12pt" },
    { label: "14", value: "14pt" },
    { label: "16", value: "16pt" },
    { label: "18", value: "18pt" },
    { label: "20", value: "20pt" },
    { label: "24", value: "24pt" },
    { label: "28", value: "28pt" },
    { label: "36", value: "36pt" },
];

/** Kroje dostępne w pasku — z pełnym stosem zapasowym, bo treść czyta się też na Debianie i telefonie. */
export const KROJE = [
    { label: "Domyślny (jak aplikacja)", value: "" },
    { label: "Times New Roman", value: "'Times New Roman', 'DejaVu Serif', serif" },
    { label: "Arial", value: "Arial, 'DejaVu Sans', sans-serif" },
    { label: "Georgia", value: "Georgia, 'DejaVu Serif', serif" },
    { label: "Courier New", value: "'Courier New', 'DejaVu Sans Mono', monospace" },
];

/** Maksymalny poziom wcięcia (tyle samo, ile natywnie obsługuje Quill). */
export const MAKS_WCIECIE = 8;

/** Jeden poziom wcięcia. `em` zamiast `px`, żeby wcięcie skalowało się razem z rozmiarem pisma. */
export const KROK_WCIECIA_EM = 2.5;

/** Poziom wcięcia → wartość CSS (`2` → `"5em"`). */
export const wciecieNaCss = (poziom) => `${(Number(poziom) || 0) * KROK_WCIECIA_EM}em`;

/**
 * Ile `em` ma jednostka (bazą dokumentu jest pismo 12 pt = 16 px; 1 in = 96 px, 1 cm = 96 / 2,54 px).
 * [EB-4 poprawki 2026-10-03] Także cm, mm i in — polski Word zapisuje wcięcie w centymetrach
 * (`margin-left: 1.25cm`), a takie wcięcie przy wklejeniu dawało 0 i znikało.
 */
const EM_W_JEDNOSTCE = { em: 1, rem: 1, px: 1 / 16, pt: 1 / 12, in: 6, cm: 6 / 2.54, mm: 6 / 25.4 };

/**
 * Wartość CSS → poziom wcięcia (`"5em"` → `2`).
 *
 * [EB-4 2026-10-03] Rozumie każde rozsądne wcięcie, nie tylko nasze wielokrotności 2,5em: treść wklejona
 * z Worda (`margin-left: 35.4pt`), zapisana kiedyś w pikselach albo po innym kroku dostaje NAJBLIŻSZY
 * poziom 1–8. Do 2026-10-03 każda inna wartość dawała 0 — i wcięcie znikało przy pierwszym otwarciu
 * treści do edycji. Mniej niż pół poziomu (1,25em) to nie wcięcie, tylko margines — zwraca 0.
 */
export const cssNaWciecie = (css) => {
    const m = /^\s*([\d.]+)\s*(em|rem|px|pt|cm|mm|in)?\s*$/i.exec(String(css || ""));
    if (!m) return 0;
    const liczba = parseFloat(m[1]);
    if (!Number.isFinite(liczba) || liczba <= 0) return 0;
    const em = liczba * EM_W_JEDNOSTCE[(m[2] || "px").toLowerCase()];
    if (em < KROK_WCIECIA_EM / 2) return 0;
    return Math.min(MAKS_WCIECIE, Math.max(1, Math.round(em / KROK_WCIECIA_EM)));
};

/**
 * Właściwość CSS, którą zapisujemy wcięcie danego elementu.
 *
 * ⚠️ POZYCJA LISTY DOSTAJE `margin-left`, NIE `padding-left`. Punktor listy stoi PRZED ramką `<li>`:
 * `padding-left` odsuwa od niego sam tekst (punktor zostaje na poziomie 0, a tekst ucieka w prawo),
 * `margin-left` przesuwa całą pozycję RAZEM z punktorem — tak, jak wygląda zagnieżdżona lista
 * w edytorze. Sprawdzone w przeglądarce 2026-10-03. Akapit, nagłówek i cytat — `padding-left`.
 */
export const wlasciwoscWciecia = (el) => (el && el.tagName === "LI" ? "margin-left" : "padding-left");

/** Elementy blokowe ze stylem — tylko one mogą nieść wcięcie wiersza. */
const BLOKOWE_ZE_STYLEM = ["p", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "div", "pre"]
    .map((t) => `${t}[style]`).join(", ");

/**
 * Klasy wcięcia Quilla (`ql-indent-N`) → styl inline, na całym poddrzewie `korzen`.
 * JEDNA POSTAĆ ZAPISU wcięcia w aplikacji (regulaminy, komunikaty, aktualności, wzory pism i procedur).
 * Inne klasy zostają nietknięte — o nich decyduje wołający.
 */
export const wciecieNaStyle = (korzen) => {
    if (!korzen || typeof korzen.querySelectorAll !== "function") return korzen;
    korzen.querySelectorAll("[class*='ql-indent-']").forEach((el) => {
        const klasy = (el.getAttribute("class") || "").split(/\s+/).filter(Boolean);
        const zostaje = [];
        klasy.forEach((k) => {
            const w = /^ql-indent-(\d+)$/.exec(k);
            if (w) {
                const poziom = Math.min(MAKS_WCIECIE, parseInt(w[1], 10));
                if (poziom > 0) el.style.setProperty(wlasciwoscWciecia(el), wciecieNaCss(poziom));
                return;
            }
            zostaje.push(k);
        });
        if (zostaje.length) el.setAttribute("class", zostaje.join(" "));
        else el.removeAttribute("class");
    });
    return korzen;
};

/**
 * Styl inline wcięcia → klasa Quilla `ql-indent-N` (tylko taką postać Quill umie zwiększać i zmniejszać).
 * Pozycja listy: `margin-left`, a gdy go nie ma — `padding-left` (tak zapisywano listy do 2026-10-03).
 * @param {object} opcje `{ marginesAkapitu: true }` — także `margin-left` akapitu (wklejanie z Worda).
 */
export const wciecieNaKlasy = (korzen, opcje = {}) => {
    if (!korzen || typeof korzen.querySelectorAll !== "function") return korzen;
    // Tylko elementy blokowe — wcięcie w Quillu jest formatem WIERSZA; klasa na `<span>` nic by nie znaczyła.
    korzen.querySelectorAll(BLOKOWE_ZE_STYLEM).forEach((el) => {
        const lista = el.tagName === "LI";
        const kandydaci = lista || opcje.marginesAkapitu ? ["margin-left", "padding-left"] : ["padding-left"];
        const wlasciwosc = kandydaci.find((w) => cssNaWciecie(el.style.getPropertyValue(w)) > 0);
        if (!wlasciwosc) return;
        const poziom = cssNaWciecie(el.style.getPropertyValue(wlasciwosc));
        Array.from(el.classList).filter((k) => /^ql-indent-\d+$/.test(k)).forEach((k) => el.classList.remove(k));
        el.classList.add(`ql-indent-${poziom}`);
        // `style.paddingLeft = ""` NIE usuwa deklaracji w każdym silniku (złapane zapadką w jsdom) —
        // zostawało podwójne wcięcie: klasa Quilla PLUS stary padding, więc tekst uciekał w prawo
        // przy każdym otwarciu treści do edycji.
        kandydaci.forEach((w) => el.style.removeProperty(w));
        if (!(el.getAttribute("style") || "").trim()) el.removeAttribute("style");
    });
    return korzen;
};

/* ── Parsowanie w izolowanym dokumencie: żaden skrypt się nie wykona, żaden obrazek nie pobierze ── */
const parsuj = (html) => {
    const doc = document.implementation.createHTMLDocument("");
    doc.body.innerHTML = String(html || "");
    return doc;
};

/** Czy napis wygląda na HTML, czy na zwykły tekst (starsze regulaminy wklejano jako czysty tekst). */
export const czyHtml = (tekst) => /<[a-z][a-z0-9]*(\s[^>]*)?\/?>/i.test(String(tekst || ""));

const escape = (s) => String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Czysty tekst → akapity. Pusty wiersz rozdziela akapity, pojedynczy łamie wiersz.
 * Bez tego regulamin zapisany kiedyś jako tekst z `\n` otwierałby się w edytorze jako
 * JEDEN nieczytelny blok — a pracownik i tak widziałby go dziś bez podziału na akapity.
 */
export const tekstNaHtml = (tekst) => {
    const s = String(tekst || "").replace(/\r\n/g, "\n").trim();
    if (!s) return "";
    return s
        .split(/\n{2,}/)
        .map((akapit) => `<p>${escape(akapit).replace(/\n/g, "<br>")}</p>`)
        .join("");
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════
   1. EDYTOR → ZAPIS (HTML przenośny)
   ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** Znaczniki listy, po których poznajemy rodzaj punktora w wyjściu Quilla 2. */
const rodzajListy = (li) => {
    const wprost = (li.getAttribute("data-list") || "").toLowerCase();
    if (wprost === "bullet" || wprost === "ordered") return wprost;
    return li.parentElement && li.parentElement.tagName === "UL" ? "bullet" : "ordered";
};

/**
 * Quill 2 buduje KAŻDĄ listę na `<ol>` i rozróżnia punktory atrybutem `data-list`, którego
 * sanityzacja przy wyświetlaniu usuwa. Rozbijamy taką listę na prawdziwe `<ul>` i `<ol>`,
 * zachowując kolejność — jedna lista mieszana daje kilka list pod rząd.
 */
const rozbijListy = (doc) => {
    doc.body.querySelectorAll("ol, ul").forEach((lista) => {
        const pozycje = Array.from(lista.children).filter((el) => el.tagName === "LI");
        if (pozycje.length === 0) return;

        const grupy = [];
        pozycje.forEach((li) => {
            const rodzaj = rodzajListy(li);
            const ostatnia = grupy[grupy.length - 1];
            if (ostatnia && ostatnia.rodzaj === rodzaj) ostatnia.pozycje.push(li);
            else grupy.push({ rodzaj, pozycje: [li] });
        });

        // Jedna jednorodna grupa o właściwym znaczniku — nie ma czego przebudowywać.
        const jednorodna = grupy.length === 1;
        const wlasciwyZnacznik = jednorodna && ((grupy[0].rodzaj === "bullet") === (lista.tagName === "UL"));
        if (jednorodna && wlasciwyZnacznik) return;

        const fragment = doc.createDocumentFragment();
        grupy.forEach((g) => {
            const nowa = doc.createElement(g.rodzaj === "bullet" ? "ul" : "ol");
            g.pozycje.forEach((li) => nowa.appendChild(li));
            fragment.appendChild(nowa);
        });
        lista.parentNode.replaceChild(fragment, lista);
    });
};

/** Klasy Quilla → style inline. Po tym kroku nic już nie zależy od arkusza edytora. */
const klasyNaStyle = (doc) => {
    // Wcięcie — wspólną funkcją, tą samą, której używa zapis wzorów pism i procedur (hrTemplateModel).
    wciecieNaStyle(doc.body);
    doc.body.querySelectorAll("[class]").forEach((el) => {
        const klasy = (el.getAttribute("class") || "").split(/\s+/).filter(Boolean);
        const zostaje = [];
        klasy.forEach((k) => {
            const align = /^ql-align-(left|center|right|justify)$/.exec(k);
            if (align) {
                el.style.textAlign = align[1];
                return;
            }
            // Wszystko inne z prefiksem `ql-` to wewnętrzne oznaczenia edytora (`ql-ui`, `ql-cursor`,
            // `ql-syntax`) — w zapisanej treści nie znaczą nic.
            if (!k.startsWith("ql-")) zostaje.push(k);
        });
        if (zostaje.length) el.setAttribute("class", zostaje.join(" "));
        else el.removeAttribute("class");
    });
};

/** Atrybuty techniczne edytora, które nie mają prawa trafić do zapisanego dokumentu. */
const ATRYBUTY_DO_USUNIECIA = ["data-list", "contenteditable", "spellcheck", "data-gramm", "data-placeholder"];

/**
 * Treść z edytora → HTML przenośny (semantyka + style inline, zero klas).
 * To jest postać, którą ZAPISUJEMY i którą widzi pracownik.
 */
export const doPrzenosnego = (htmlZEdytora) => {
    const doc = parsuj(htmlZEdytora);

    // Elementy pomocnicze Quilla 2 (m.in. `<span class="ql-ui">` wstrzykiwany do KAŻDEJ pozycji listy).
    doc.body.querySelectorAll("span.ql-ui, span.ql-cursor").forEach((el) => el.remove());

    rozbijListy(doc);
    klasyNaStyle(doc);

    doc.body.querySelectorAll("*").forEach((el) => {
        ATRYBUTY_DO_USUNIECIA.forEach((a) => el.removeAttribute(a));
        if (el.getAttribute("style") === "") el.removeAttribute("style");
    });

    // Pusty akapit z jednym `<br>` to normalny sposób zapisu pustej linii — zostawiamy.
    return doc.body.innerHTML.trim();
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════
   2. ZAPIS → EDYTOR
   ═══════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Zapisana treść → postać, którą rozumie edytor.
 *
 * `<ul>`/`<ol>`, style inline (rozmiar, krój, kolor, wyrównanie) Quill czyta sam — wcięcie musi
 * wrócić do postaci klasowej, bo tylko taką Quill potrafi zwiększać i zmniejszać przyciskiem.
 * Czysty tekst (tak zapisano regulamin przed tą zmianą) dostaje podział na akapity.
 */
export const doEdytora = (zapisane) => {
    const surowe = String(zapisane || "");
    if (!surowe.trim()) return "";
    if (!czyHtml(surowe)) return tekstNaHtml(surowe);

    const doc = parsuj(surowe);
    wciecieNaKlasy(doc.body);
    return doc.body.innerHTML;
};

/**
 * [EB-4 2026-10-03] Klasy wcięcia → styl inline w GOTOWYM napisie HTML (do wyświetlenia poza edytorem,
 * np. na kanwie konfiguratora pism, gdzie nie ma arkusza Quilla). Bez dokumentu (SSR) — bez zmian.
 */
export const htmlWciecNaStyle = (html) => {
    const surowe = String(html || "");
    if (!surowe.includes("ql-indent-") || typeof document === "undefined") return surowe;
    const doc = parsuj(surowe);
    wciecieNaStyle(doc.body);
    return doc.body.innerHTML;
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════
   3. WKLEJANIE Z WORDA
   ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** Najbliższy dostępny rozmiar w punktach — Word wkleja m.in. `11.0pt`, `14.5px`, `1.5em`. */
export const najblizszyRozmiar = (wartoscCss) => {
    const m = /^\s*([\d.]+)\s*(pt|px|em|rem)?\s*$/i.exec(String(wartoscCss || ""));
    if (!m) return null;
    const liczba = parseFloat(m[1]);
    if (!Number.isFinite(liczba) || liczba <= 0) return null;
    const jednostka = (m[2] || "px").toLowerCase();
    // px → pt (72/96), em/rem liczone od 12 pt, czyli od rozmiaru bazowego tekstu dokumentu.
    const pt = jednostka === "pt" ? liczba
        : jednostka === "px" ? liczba * 0.75
            : liczba * 12;
    let najlepszy = null;
    let najmniejszaRoznica = Infinity;
    ROZMIARY_PT.forEach((r) => {
        const roznica = Math.abs(parseFloat(r.value) - pt);
        if (roznica < najmniejszaRoznica) { najmniejszaRoznica = roznica; najlepszy = r.value; }
    });
    return najlepszy;
};

/** Deklaracje CSS, które Word wysypuje w każdym akapicie, a które nam nic nie mówią. */
const CSS_DO_WYRZUCENIA = /^(mso-|tab-stops|text-autospace|page-break|font-variant|letter-spacing|word-spacing|line-height|widows|orphans|border|background|position|z-index|float|clear|display|width|height|max-|min-)/i;

/** Znak punktora z listy Worda — akapit zaczyna się od niego, bo Word nie używa `<li>`. */
const PUNKTOR = /^\s*([•·▪◦o§*-]|[a-zA-Z][.)]|\d+[.)])\s+/;

const czysciStyl = (el) => {
    const styl = el.getAttribute("style");
    if (!styl) return;
    const zostaje = [];
    styl.split(";").forEach((deklaracja) => {
        const idx = deklaracja.indexOf(":");
        if (idx < 0) return;
        const wlasciwosc = deklaracja.slice(0, idx).trim().toLowerCase();
        const wartosc = deklaracja.slice(idx + 1).trim();
        if (!wlasciwosc || !wartosc) return;
        if (CSS_DO_WYRZUCENIA.test(wlasciwosc)) return;

        if (wlasciwosc === "font-size") {
            const rozmiar = najblizszyRozmiar(wartosc);
            if (rozmiar) zostaje.push(`font-size: ${rozmiar}`);
            return;
        }
        if (wlasciwosc === "font-weight") {
            if (/^(bold|[6-9]00)$/i.test(wartosc)) zostaje.push("font-weight: bold");
            return;
        }
        if (["color", "text-align", "font-family", "font-style", "text-decoration", "padding-left", "margin-left"].includes(wlasciwosc)) {
            zostaje.push(`${wlasciwosc}: ${wartosc}`);
        }
    });
    if (zostaje.length) el.setAttribute("style", zostaje.join("; "));
    else el.removeAttribute("style");
};

/**
 * Akapity listy z Worda (`<p style="mso-list:l0 level1">• tekst</p>`) → prawdziwe `<ul>`/`<ol>`.
 * Word NIE wysyła `<li>`, tylko akapity z ręcznie wpisanym punktorem — bez tej zamiany wklejona
 * lista traci punktory i wcięcia, a w tekście zostają gołe znaki „•".
 */
const listyZWorda = (doc) => {
    const akapity = Array.from(doc.body.querySelectorAll("p"));
    let biezaca = null;
    let rodzajBiezacej = null;

    akapity.forEach((p) => {
        const styl = p.getAttribute("style") || "";
        const tekst = p.textContent || "";
        const msoLista = /mso-list\s*:/i.test(styl) || /^Mso(ListParagraph|ListBullet|ListNumber)/i.test(p.getAttribute("class") || "");
        const dopasowanie = PUNKTOR.exec(tekst);
        if (!msoLista || !dopasowanie) { biezaca = null; rodzajBiezacej = null; return; }

        const rodzaj = /^\s*[•·▪◦o§*-]\s/.test(tekst) ? "bullet" : "ordered";
        const poziomM = /level(\d+)/i.exec(styl);
        const poziom = poziomM ? Math.min(MAKS_WCIECIE, Math.max(1, parseInt(poziomM[1], 10))) - 1 : 0;

        const li = doc.createElement("li");
        li.innerHTML = p.innerHTML;
        // Punktor wpisany przez Worda jako TEKST — usuwamy go, bo znacznik listy narysuje własny.
        const pierwszy = li.firstChild;
        if (pierwszy) {
            if (pierwszy.nodeType === 3) pierwszy.textContent = pierwszy.textContent.replace(PUNKTOR, "");
            else if (pierwszy.textContent && PUNKTOR.test(pierwszy.textContent)) pierwszy.remove();
        }
        if (PUNKTOR.test(li.textContent || "")) li.innerHTML = li.innerHTML.replace(PUNKTOR, "");
        if (poziom > 0) li.classList.add(`ql-indent-${poziom}`);

        if (!biezaca || rodzajBiezacej !== rodzaj) {
            biezaca = doc.createElement(rodzaj === "bullet" ? "ul" : "ol");
            rodzajBiezacej = rodzaj;
            p.parentNode.insertBefore(biezaca, p);
        }
        biezaca.appendChild(li);
        p.remove();
    });
};

/**
 * Sprząta HTML wklejony z Worda / Google Docs / strony WWW, ZANIM zobaczy go edytor.
 *
 * Zostawia to, co niesie treść (pogrubienie, kursywa, listy, nagłówki, rozmiar, kolor, wyrównanie),
 * a wyrzuca aparaturę edytora tekstu: arkusze `<style>`, komentarze warunkowe, `<o:p>`, klasy `Mso*`
 * i setki deklaracji `mso-*`. Bez tego jedno wklejenie umowy potrafi wnieść do regulaminu kilkadziesiąt
 * kilobajtów śmieci i tło rodem z Worda.
 */
export const normalizujWklejonyHtml = (html) => {
    let surowe = String(html || "");
    // Komentarze (w tym warunkowe `<!--[if gte mso 9]>`) — usuwane tekstowo, bo w środku bywa cały arkusz.
    surowe = surowe.replace(/<!--[\s\S]*?-->/g, "");
    surowe = surowe.replace(/<\/?o:[a-z]+[^>]*>/gi, "");
    surowe = surowe.replace(/<\/?[a-z]+:[a-z]+[^>]*>/gi, "");

    const doc = parsuj(surowe);
    doc.body.querySelectorAll("style, meta, link, title, script, xml").forEach((el) => el.remove());

    listyZWorda(doc);

    doc.body.querySelectorAll("*").forEach((el) => {
        czysciStyl(el);
        const klasy = (el.getAttribute("class") || "").split(/\s+/).filter((k) => k.startsWith("ql-indent-"));
        if (klasy.length) el.setAttribute("class", klasy.join(" "));
        else el.removeAttribute("class");
        ["lang", "dir", "align", "valign", "width", "height", "border", "cellspacing", "cellpadding", "id", "name"].forEach((a) => el.removeAttribute(a));
        Array.from(el.attributes || []).forEach((attr) => {
            if (/^data-/i.test(attr.name) || /^on/i.test(attr.name)) el.removeAttribute(attr.name);
        });
    });

    // Akapity, w których po sprzątaniu nie ma nic poza spacją twardą — Word wstawia ich mnóstwo.
    doc.body.querySelectorAll("p, div").forEach((el) => {
        if (!el.querySelector("img, br, table") && !(el.textContent || "").replace(/ |\s/g, "")) el.remove();
    });

    // [EB-4] Wcięty akapit Worda (`margin-left: 35.4pt`) → poziom wcięcia edytora. Bez tego Quill
    // wyrzucał nieznany mu styl i wklejony dokument tracił wszystkie wcięcia akapitów.
    wciecieNaKlasy(doc.body, { marginesAkapitu: true });

    return doc.body.innerHTML;
};

const htmlPrzenosny = {
    ROZMIARY_PT, KROJE, MAKS_WCIECIE, KROK_WCIECIA_EM,
    czyHtml, tekstNaHtml, doPrzenosnego, doEdytora, normalizujWklejonyHtml,
    wciecieNaCss, cssNaWciecie, najblizszyRozmiar,
    wciecieNaStyle, wciecieNaKlasy, htmlWciecNaStyle, wlasciwoscWciecia,
};
export default htmlPrzenosny;
